import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { controlBase, controlOpen, controlSizes, type ControlSize } from "../lib/control";
import { useIsMobile } from "../lib/use-is-mobile";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";
import { useFieldControl } from "./field";
import { Popover } from "./popover";

export interface DatePickerProps {
  value?: string; // Format YYYY-MM-DD
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  size?: ControlSize;
  disabled?: boolean;
  min?: string;
  max?: string;
  className?: string;
  id?: string;
}

const MONTH_NAMES = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const WEEKDAYS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

function parseDateStr(str?: string): Date | null {
  if (!str) return null;
  const parts = str.split("-");
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0]!, 10);
  const month = parseInt(parts[1]!, 10) - 1;
  const day = parseInt(parts[2]!, 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month, day);
}

function formatDateStr(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDisplayDate(str?: string): string {
  const parsed = parseDateStr(str);
  if (!parsed) return "";
  return parsed.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Sélecteur de date 100% fait maison.
 * Remplace l'input HTML5 natif par un calendrier riche, accessible et responsive.
 */
export function DatePicker({
  value,
  defaultValue,
  onChange,
  placeholder = "Sélectionner une date…",
  size = "md",
  disabled,
  min,
  max,
  className,
  id,
  ...props
}: DatePickerProps) {
  const [internal, setInternal] = useState<string>(defaultValue ?? "");
  const isControlled = value !== undefined;
  const currentDateStr = isControlled ? value : internal;

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Mois & Année affichés dans le calendrier
  const selectedDate = parseDateStr(currentDateStr) ?? new Date();
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());

  const fieldProps = useFieldControl(props);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (currentDateStr) {
      const d = parseDateStr(currentDateStr);
      if (d) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [currentDateStr]);

  const selectDate = (dateStr: string) => {
    if (!isControlled) setInternal(dateStr);
    onChange?.(dateStr);
    setIsOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // Calcul des jours du mois
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  let startingDayOfWeek = firstDayOfMonth.getDay() - 1; // 0 = Lundi, 6 = Dimanche
  if (startingDayOfWeek === -1) startingDayOfWeek = 6;

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const days: { dateStr: string; dayNum: number; isCurrentMonth: boolean }[] = [];

  // Jours du mois précédent
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    const prevDate = new Date(viewYear, viewMonth - 1, daysInPrevMonth - i);
    days.push({
      dateStr: formatDateStr(prevDate),
      dayNum: daysInPrevMonth - i,
      isCurrentMonth: false,
    });
  }

  // Jours du mois courant
  for (let d = 1; d <= daysInMonth; d++) {
    const curDate = new Date(viewYear, viewMonth, d);
    days.push({
      dateStr: formatDateStr(curDate),
      dayNum: d,
      isCurrentMonth: true,
    });
  }

  // Remplissage pour compléter la grille 7x6 (42 cases)
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    const nextDate = new Date(viewYear, viewMonth + 1, i);
    days.push({
      dateStr: formatDateStr(nextDate),
      dayNum: i,
      isCurrentMonth: false,
    });
  }

  const todayStr = formatDateStr(new Date());

  const selectToday = () => selectDate(todayStr);
  const selectTomorrow = () => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    selectDate(formatDateStr(t));
  };
  const selectNextWeek = () => {
    const t = new Date();
    t.setDate(t.getDate() + 7);
    selectDate(formatDateStr(t));
  };
  const clearSelection = () => selectDate("");

  const calendarContent = (
    <div>
      {/* Navigation Mois & Année */}
      <div className="flex items-center justify-between gap-2 border-b border-border pb-3">
        <Button size="icon-sm" variant="ghost" onClick={prevMonth} aria-label="Mois précédent">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="size-4">
            <path d="M12 15l-5-5 5-5" />
          </svg>
        </Button>
        <span className="text-sm font-semibold text-fg">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <Button size="icon-sm" variant="ghost" onClick={nextMonth} aria-label="Mois suivant">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="size-4">
            <path d="M8 5l5 5-5 5" />
          </svg>
        </Button>
      </div>

      {/* En-tête des jours de la semaine */}
      <div className="mt-3 grid grid-cols-7 text-center">
        {WEEKDAYS.map((w) => (
          <span key={w} className="text-[11px] font-semibold uppercase text-fg-muted">
            {w}
          </span>
        ))}
      </div>

      {/* Grille des jours */}
      <div className="mt-2 grid grid-cols-7 gap-1">
        {days.map(({ dateStr, dayNum, isCurrentMonth }) => {
          const isSelected = dateStr === currentDateStr;
          const isToday = dateStr === todayStr;
          const isDisabled = Boolean((min && dateStr < min) || (max && dateStr > max));

          return (
            <button
              key={dateStr}
              type="button"
              disabled={isDisabled}
              onClick={() => selectDate(dateStr)}
              className={cn(
                "relative mx-auto flex size-9 items-center justify-center rounded-lg text-xs font-medium transition-colors touch-manipulation sm:size-8",
                // Grille dense : le curseur y traverse plusieurs cases par
                // seconde. `tint` fait fondre un calque déjà peint plutôt que
                // de repeindre le fond de chaque case au passage.
                isCurrentMonth && !isSelected && "tint",
                !isCurrentMonth && "text-fg-subtle/40",
                isCurrentMonth && !isSelected && "text-fg",
                isSelected && "bg-accent font-semibold text-fg-on-accent shadow-xs",
                isToday && !isSelected && "border border-accent font-semibold text-accent",
                isDisabled && "pointer-events-none opacity-30 cursor-not-allowed",
              )}
            >
              {dayNum}
            </button>
          );
        })}
      </div>

      {/* Raccourcis rapides */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-1 border-t border-border pt-3">
        <Button size="xs" variant="ghost" onClick={selectToday}>
          Aujourd'hui
        </Button>
        <Button size="xs" variant="ghost" onClick={selectTomorrow}>
          Demain
        </Button>
        <Button size="xs" variant="ghost" onClick={selectNextWeek}>
          +7 jours
        </Button>
        {currentDateStr && (
          <Button size="xs" variant="ghost" onClick={clearSelection} className="text-danger">
            Effacer
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          controlBase,
          controlSizes[size],
          "flex cursor-pointer items-center justify-between gap-2 text-left touch-manipulation",
          isOpen && controlOpen,
          className,
        )}
        {...fieldProps}
      >
        <span className={cn("truncate", !currentDateStr && "text-fg-muted")}>
          {currentDateStr ? formatDisplayDate(currentDateStr) : placeholder}
        </span>
        <svg
          className="size-4 shrink-0 text-fg-subtle"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="14" height="13" rx="2" />
          <path d="M7 2v4M13 2v4M3 8h14" />
        </svg>
      </button>

      {/* Affichage Mobile : Vrai BottomSheet Vaul swipable */}
      {isMobile ? (
        <BottomSheet
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title="Sélectionner une date"
        >
          {calendarContent}
        </BottomSheet>
      ) : (
        /* Affichage Desktop : panneau flottant portalisé dans la top layer */
        <Popover anchorRef={containerRef} open={isOpen} onClose={() => setIsOpen(false)} width={320}>
          <div className="p-4">{calendarContent}</div>
        </Popover>
      )}
    </div>
  );
}
